import { useStore } from "@tanstack/react-store"
import { signal } from "~/sdk/signal"
import type { ImageWidget } from "~/types/widgets"
import type { Scheduling } from "~/sdk/contentScheduling"

/**
 * @titleBy question
 */
export interface Question {
    /**
     * @title Pergunta
     */
    question: string

    /**
     * @title Resposta
     * @format html
     */
    answer: string

    /**
     * @title Agendamento
     * @description Deixe este campo vazio para manter sempre visível
     */
    scheduling?: Scheduling
}

export interface Group {
    /**
     * @title Título
     */
    label: string

    /**
     * @title Ícone
     */
    icon: ImageWidget

    /**
     * @title Largura do ícone (em px, padrão 40px)
     */
    width?: number

    /**
     * @title Altura do ícone (em px, padrão 40px)
     */
    height?: number

    /**
     * @title Perguntas
     */
    questions: Question[]
}

const selectedGroup = signal<Group | null>(null)
const search = signal<{ query: string; result: Question[] | null }>({ query: '', result: null })

const scrollToQuestions = () => {
    const questions = document.querySelector('#questions')
    questions?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const setSelectedGroup = (group: Group | null) => {
    selectedGroup.value = group
    search.value = { query: '', result: null }
    scrollToQuestions()
}

const setSearch = (query: string, result: Question[] | null) => {
    selectedGroup.value = null
    search.value = { query, result }
    scrollToQuestions()
}

export const useFAQ = () => {
    // The module-level `signal()` is @tanstack/store-backed and does NOT
    // auto-subscribe on `.value` reads inside render — subscribe explicitly
    // so consumers re-render on selection/search changes (same as useUser).
    //
    // Return the values `useStore` produced rather than re-reading `.value`:
    // the React Compiler treats a module-scope `.value` read as a constant and
    // memoizes the returned object with no dependencies, so consumers would
    // keep receiving the first (empty) selection forever even though they do
    // re-render.
    const currentGroup = useStore(selectedGroup.store, (s) => s)
    const currentSearch = useStore(search.store, (s) => s)

    return {
        selectedGroup: currentGroup,
        setSelectedGroup,
        search: currentSearch,
        setSearch,
    }
}
